import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  User, Users, Phone, Mail, Calendar, FileText, Plus, Clock,
  ChevronRight, Link as LinkIcon, Building, CreditCard, MapPin,
  BarChart3, Star, TrendingUp, ChevronDown, ChevronUp, ShieldAlert, ShieldCheck,
  AlertTriangle, CheckCircle, Info, Wallet
} from 'lucide-react';
import toast from 'react-hot-toast';
import api, { beneficiariosAPI, inadimplenciaAPI } from '../services/api';
import CartoesSaude from '../components/CartoesSaude';
import AlertasBeneficiario from '../components/AlertasBeneficiario';
import AvisoDefasagem from '../components/AvisoDefasagem';
import CuidadosIdss from '../components/CuidadosIdss';
import { useAuth } from '../context/AuthContext';
import {
  FAIXAS_ATRASO, faixaDoAtraso, PESO_DEPENDENTE, PESO_CONTRATO_SUBORDINADO,
  PESO_CARENCIA, ehMensalidade, consequenciaContratual,
} from '../utils/scoreFinanceiro';

// As regras do score (faixas, pesos, consequência contratual) vivem em
// utils/scoreFinanceiro.js — a BUSCA usa as MESMAS, então a legenda daqui não
// tem como divergir do alerta de lá.

// ─── Componente de Score de Saúde Financeira ────────────────────────────────
const ScoreSaude = ({ maiorAtraso, qtdCarenciasAtivas, qtdDependentes, qtdContratosSubordinados,
                      dividas, podeVerModulo, onVerModulo }) => {
  const [open, setOpen] = useState(false);
  const [verRegras, setVerRegras] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Calcular score e fatores
  const fatores = [];
  let score = 100;

  const faixa = faixaDoAtraso(maiorAtraso);
  // Só há dívida de mensalidade? (define a consequência contratual exibida)
  const tiposEmAtraso = (dividas || []).flatMap((b) => b.tipos || []);
  const temMensalidade = tiposEmAtraso.some(ehMensalidade);
  const soCoparticipacao = tiposEmAtraso.length > 0 && !temMensalidade;

  if (faixa.peso !== 0) {
    score += faixa.peso;
    const consequencia = consequenciaContratual(maiorAtraso, !soCoparticipacao);
    fatores.push({
      cor: faixa.cor === '#22c55e' ? '#eab308' : faixa.cor,
      desc: `${maiorAtraso} dias em atraso (${faixa.texto})${consequencia}`,
      peso: faixa.peso,
    });
  }

  if (qtdCarenciasAtivas > 0 && maiorAtraso === 0) {
    score += PESO_CARENCIA;
    fatores.push({ cor: '#3b82f6', desc: `${qtdCarenciasAtivas} carência${qtdCarenciasAtivas > 1 ? 's' : ''} ativa${qtdCarenciasAtivas > 1 ? 's' : ''} sem inadimplência`, peso: PESO_CARENCIA });
  }

  if (qtdDependentes > 0) {
    const desconto = qtdDependentes * PESO_DEPENDENTE;
    score += desconto;
    fatores.push({ cor: '#8b5cf6', desc: `${qtdDependentes} dependente${qtdDependentes > 1 ? 's' : ''} vinculado${qtdDependentes > 1 ? 's' : ''} (${PESO_DEPENDENTE} cada)`, peso: desconto });
  }

  if (qtdContratosSubordinados > 0) {
    const desconto = qtdContratosSubordinados * PESO_CONTRATO_SUBORDINADO;
    score += desconto;
    fatores.push({ cor: '#06b6d4', desc: `${qtdContratosSubordinados} contrato${qtdContratosSubordinados > 1 ? 's' : ''} subordinado${qtdContratosSubordinados > 1 ? 's' : ''} (${PESO_CONTRATO_SUBORDINADO} cada)`, peso: desconto });
  }

  score = Math.max(0, score);

  // Faixa de cor pela pontuação...
  let cor, label, nivelPorScore;
  if (score >= 85)      { cor = '#22c55e'; label = 'REGULAR'; nivelPorScore = 0; }
  else if (score >= 60) { cor = '#eab308'; label = 'ATENÇÃO'; nivelPorScore = 2; }
  else if (score >= 35) { cor = '#f97316'; label = 'ALERTA';  nivelPorScore = 2; }
  else                  { cor = '#ef4444'; label = 'CRÍTICO'; nivelPorScore = 3; }

  // ...mas o ATRASO impõe um PISO: >60 dias nunca é melhor que CRÍTICO e
  // 40–60 nunca é melhor que ATENÇÃO, independentemente dos outros fatores.
  if (faixa.nivel > nivelPorScore) {
    cor = faixa.cor;
    label = faixa.rotulo;
  }

  const showAmplitude = (qtdDependentes > 0 || qtdContratosSubordinados > 0) && maiorAtraso > 0;

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
      <button
        onClick={() => setOpen(v => !v)}
        title="Clique para ver detalhes do score financeiro"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '5px',
          background: `${cor}18`, border: `1px solid ${cor}60`,
          borderRadius: '12px', padding: '3px 10px 3px 8px',
          cursor: 'pointer', fontSize: '0.73rem', fontWeight: 700, color: cor,
          outline: 'none',
        }}
      >
        <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: cor, flexShrink: 0 }} />
        {label}
        <span style={{ opacity: 0.5, fontWeight: 400, margin: '0 1px' }}>·</span>
        <span>{score}</span>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 8px)', left: '50%', transform: 'translateX(-50%)',
          background: '#fff', border: '1px solid #e8e8e8', borderRadius: '14px',
          boxShadow: '0 10px 30px rgba(0,0,0,0.13)', padding: '18px',
          // largura FIXA + border-box: com min/max o grid interno alargava o cartão
          // quando um rótulo era longo e os valores saíam pela borda direita (issue #79)
          width: '330px', maxWidth: 'calc(100vw - 24px)', boxSizing: 'border-box', zIndex: 1000,
        }}>
          {/* Título */}
          <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '14px' }}>
            Saúde Financeira
          </div>

          {/* Score + barra */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '14px' }}>
            <div style={{ fontSize: '2.4rem', fontWeight: 800, color: cor, lineHeight: 1, minWidth: '52px' }}>{score}</div>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.68rem', color: '#aaa', marginBottom: '5px' }}>
                <span style={{ fontWeight: 700, color: cor }}>{label}</span>
                <span>/ 100</span>
              </div>
              <div style={{ background: '#f0f0f0', borderRadius: '6px', height: '8px', overflow: 'hidden' }}>
                <div style={{
                  width: `${score}%`, height: '100%', borderRadius: '6px',
                  background: `linear-gradient(90deg, ${cor}99, ${cor})`,
                  transition: 'width 0.5s ease',
                }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.6rem', color: '#ddd', marginTop: '3px' }}>
                <span>0</span><span>35</span><span>60</span><span>85</span><span>100</span>
              </div>
            </div>
          </div>

          {/* Fatores */}
          {fatores.length > 0 ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '5px', marginBottom: showAmplitude ? '12px' : 0 }}>
              {fatores.map((f, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'flex-start', gap: '8px', minWidth: 0,
                  padding: '7px 10px', background: '#fafafa',
                  borderRadius: '8px', border: `1px solid ${f.cor}22`,
                }}>
                  <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: f.cor, flexShrink: 0, marginTop: '4px' }} />
                  <span style={{ flex: 1, minWidth: 0, fontSize: '0.77rem', color: '#444', lineHeight: 1.4, overflowWrap: 'anywhere' }}>{f.desc}</span>
                  <span style={{ fontSize: '0.73rem', fontWeight: 700, color: f.cor, flexShrink: 0 }}>{f.peso}</span>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '0.82rem', color: '#22c55e', padding: '8px 10px', background: '#f0fdf4', borderRadius: '8px', marginBottom: showAmplitude ? '12px' : 0 }}>
              <CheckCircle size={14} /> Nenhum fator de risco identificado
            </div>
          )}

          {/* O que compõe a dívida — o atendente precisa saber O QUE é,
              de QUANDO (competência) e há QUANTOS dias. */}
          {maiorAtraso > 0 && (
            <div style={{ marginTop: '12px', marginBottom: '12px' }}>
              <div style={{ fontSize: '0.66rem', fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '7px' }}>
                Composição da dívida
              </div>
              {dividas === null ? (
                <div style={{ fontSize: '0.75rem', color: '#bbb', padding: '7px 10px', background: '#fafafa', borderRadius: '8px' }}>
                  Detalhe não disponível para o seu perfil.
                </div>
              ) : (dividas || []).length === 0 ? (
                <div style={{ fontSize: '0.75rem', color: '#bbb', padding: '7px 10px', background: '#fafafa', borderRadius: '8px' }}>
                  Sem detalhamento retornado.
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '5px' }}>
                  {(dividas || []).slice(0, 6).map((b, i) => {
                    const tipos = (b.tipos || []).join(' + ') || '—';
                    const c = faixaDoAtraso(b.dias).cor;
                    return (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 10px', minWidth: 0,
                                            background: '#fafafa', borderRadius: '8px', border: `1px solid ${c}22` }}>
                        <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: c, flexShrink: 0 }} />
                        <span style={{ flex: 1, minWidth: 0, fontSize: '0.76rem', color: '#444', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {tipos}
                          {b.refer && <span style={{ color: '#999' }}> · ref {b.refer}</span>}
                        </span>
                        <span style={{ fontSize: '0.73rem', fontWeight: 700, color: c, flexShrink: 0 }}>{b.dias}d</span>
                        {b.val > 0 && (
                          <span style={{ fontSize: '0.72rem', color: '#666', flexShrink: 0, minWidth: '62px', textAlign: 'right' }}>
                            {b.val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                          </span>
                        )}
                      </div>
                    );
                  })}
                  {(dividas || []).length > 6 && (
                    <div style={{ fontSize: '0.7rem', color: '#aaa', textAlign: 'right' }}>
                      +{(dividas || []).length - 6} outros títulos
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Alerta de amplitude */}
          {showAmplitude && (
            <div style={{
              background: `${cor}10`, border: `1px solid ${cor}40`,
              borderRadius: '9px', padding: '10px 12px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.73rem', fontWeight: 700, color: cor, marginBottom: '5px' }}>
                <AlertTriangle size={13} /> Amplitude de impacto
              </div>
              <div style={{ fontSize: '0.75rem', color: '#555', lineHeight: 1.5 }}>
                Um cancelamento impactaria{' '}
                {qtdDependentes > 0 && (
                  <strong>{qtdDependentes} dependente{qtdDependentes > 1 ? 's' : ''}</strong>
                )}
                {qtdDependentes > 0 && qtdContratosSubordinados > 0 && ' e '}
                {qtdContratosSubordinados > 0 && (
                  <strong>{qtdContratosSubordinados} contrato{qtdContratosSubordinados > 1 ? 's' : ''} subordinado{qtdContratosSubordinados > 1 ? 's' : ''}</strong>
                )}
                {' '}além deste titular.
              </div>
            </div>
          )}

          {/* Legenda das regras — o atendente tem que saber COMO o número sai */}
          <div style={{ marginTop: '12px', borderTop: '1px solid #f0f0f0', paddingTop: '10px' }}>
            <button onClick={() => setVerRegras((v) => !v)}
                    style={{ display: 'flex', alignItems: 'center', gap: '5px', background: 'none', border: 'none',
                             padding: 0, cursor: 'pointer', fontSize: '0.72rem', color: '#888', fontWeight: 600 }}>
              <Info size={12} /> Como este score é calculado
              {verRegras ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            </button>

            {verRegras && (
              <div style={{ marginTop: '8px', fontSize: '0.72rem', color: '#666', lineHeight: 1.55 }}>
                <div style={{ marginBottom: '6px' }}>Começa em <strong>100</strong> e desconta:</div>
                <div style={{ display: 'grid', gap: '3px', marginBottom: '8px' }}>
                  {FAIXAS_ATRASO.filter((f) => f.peso !== 0).map((f) => (
                    <div key={f.min} style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                      <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: f.cor, flexShrink: 0 }} />
                      <span style={{ flex: 1 }}>Atraso de <strong>{f.texto}</strong> → {f.rotulo}</span>
                      <strong style={{ color: f.cor }}>{f.peso}</strong>
                    </div>
                  ))}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                    <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#8b5cf6', flexShrink: 0 }} />
                    <span style={{ flex: 1 }}>Cada dependente vinculado</span><strong style={{ color: '#8b5cf6' }}>{PESO_DEPENDENTE}</strong>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                    <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#06b6d4', flexShrink: 0 }} />
                    <span style={{ flex: 1 }}>Cada contrato subordinado</span><strong style={{ color: '#06b6d4' }}>{PESO_CONTRATO_SUBORDINADO}</strong>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                    <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#3b82f6', flexShrink: 0 }} />
                    <span style={{ flex: 1 }}>Carência ativa sem inadimplência</span><strong style={{ color: '#3b82f6' }}>{PESO_CARENCIA}</strong>
                  </div>
                </div>
                <div style={{ background: '#fafafa', borderRadius: '8px', padding: '8px 10px' }}>
                  <div style={{ marginBottom: '4px' }}>
                    <strong>O atraso define o mínimo da cor.</strong> Acima de 60 dias nunca fica melhor que vermelho; de 40 a 60, nunca melhor que amarelo. Dependentes e contratos podem agravar (nunca suavizar).
                  </div>
                  <div>
                    <strong>Mensalidade × coparticipação:</strong> só o atraso de <strong>mensalidade</strong> enseja cancelamento contratual; coparticipação em atraso, não.
                  </div>
                </div>
              </div>
            )}

            {podeVerModulo && (
              <button onClick={onVerModulo}
                      style={{ marginTop: '10px', width: '100%', padding: '7px', borderRadius: '8px', cursor: 'pointer',
                               border: '1px solid #e0e0e0', background: '#fff', color: 'var(--brand, #20a490)',
                               fontSize: '0.76rem', fontWeight: 600, display: 'flex', alignItems: 'center',
                               justifyContent: 'center', gap: '6px' }}>
                Ver no módulo de Inadimplência <ChevronRight size={13} />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
// ────────────────────────────────────────────────────────────────────────────

const BeneficiarioDetalhe = () => {
  const { cpf } = useParams();
  const navigate = useNavigate();
  const { temNivel, podeModulo } = useAuth();
  // O atalho só aparece pra quem tem o módulo de Inadimplência (nível do módulo,
  // não cargo — ADR 0013). Botão que dá "acesso negado" é pior que botão nenhum.
  const podeVerInadimplencia = temNivel('dashboard_inadimplencia', 'operador');
  const [loading, setLoading] = useState(true);
  const [dados, setDados] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [carencias, setCarencias] = useState([]);
  const [carenciasErro, setCarenciasErro] = useState(null);
  const [carenciasCarregadas, setCarenciasCarregadas] = useState(false);
  const [inadimplencia, setInadimplencia] = useState({ inadimplente: false, maior_atraso_dias: 0 });
  // null = sem permissão/falhou; [] = sem detalhe; [...] = títulos em atraso
  const [dividas, setDividas] = useState([]);
  const [showDashboard, setShowDashboard] = useState(true);
  const [showAllAtendimentos, setShowAllAtendimentos] = useState(false);

  useEffect(() => {
    loadDados();
    loadDashboard();
    loadCarencias();
  }, [cpf]);

  const loadDados = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/beneficiarios/cpf/${cpf}/detalhe`);
      setDados(response.data);
      // Checar inadimplência assim que tivermos a matrícula.
      // ATENÇÃO: a inadimplência é chaveada pela matrícula SOUL (mat_resp_fin),
      // não pela matrícula do ESAU — usar a errada faz inadimplente aparecer
      // como REGULAR. Ver docs/backlog-ame-crm.md (Sit. 12).
      const matricula = response.data?.beneficiario?.matricula_soul
                     || response.data?.beneficiario?.matricula;
      if (matricula) {
        inadimplenciaAPI.checkBeneficiario(matricula)
          .then(r => setInadimplencia(r.data))
          .catch(() => {});
        // Detalhe das dívidas (o que é, competência, dias) para o painel do score.
        // Perfis sem acesso ao financeiro recebem 403 — a cor/score continua,
        // só o detalhe não aparece.
        inadimplenciaAPI.boletosRf(matricula)
          .then(r => setDividas(Array.isArray(r.data) ? r.data : (r.data?.boletos || [])))
          .catch(() => setDividas(null));
      }
    } catch (error) {
      toast.error('Erro ao carregar dados do beneficiario');
      console.error(error);
    }
    setLoading(false);
  };

  const loadCarencias = async () => {
    setCarenciasErro(null);
    try {
      const response = await api.get(`/beneficiarios/cpf/${cpf}/carencias`);
      setCarencias(response.data || []);
    } catch (error) {
      // Não é mais silencioso: registra e sinaliza o estado de falha para
      // conseguirmos distinguir "sem carências" de "consulta falhou (FDW/view)".
      const status = error?.response?.status;
      const detalhe = error?.response?.data?.detail || error?.message || 'erro desconhecido';
      console.error(`[carencias] falha ao consultar (${status || 'sem status'}):`, detalhe);
      setCarencias([]);
      setCarenciasErro(detalhe);
    } finally {
      setCarenciasCarregadas(true);
    }
  };

  const loadDashboard = async () => {
    try {
      const response = await beneficiariosAPI.getDashboard(cpf);
      setDashboard(response.data);
    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
    }
  };

  const formatHoras = (horas) => {
    if (horas === null || horas === undefined) return '-';
    if (horas === 0) return '0h';
    if (horas < 1) return `${Math.round(horas * 60)}min`;
    const h = Math.floor(horas);
    const m = Math.round((horas - h) * 60);
    if (m === 0) return `${h}h`;
    return `${h}h ${m}min`;
  };

  const getNPSColor = (nota) => {
    if (nota >= 9) return '#27ae60';
    if (nota >= 7) return '#f39c12';
    return '#e74c3c';
  };

  const getNPSLabel = (nota) => {
    if (nota >= 9) return 'Promotor';
    if (nota >= 7) return 'Neutro';
    return 'Detrator';
  };

  const canalLabels = {
    TELEFONICO: 'Telefone', PRESENCIAL: 'Presencial', EMAIL: 'E-mail',
    APP: 'App', CHAT: 'Chat', WHATSAPP: 'WhatsApp',
  };

  const statusLabels = {
    ABERTO: 'Aberto', EM_ANDAMENTO: 'Em Andamento', FINALIZADO: 'Finalizado',
    TRANSFERIDO: 'Transferido', AGUARDANDO_RETORNO: 'Aguardando',
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const parts = dateStr.split('/');
      if (parts.length === 3) {
        return dateStr;
      }
      return new Date(dateStr).toLocaleDateString('pt-BR');
    } catch {
      return dateStr;
    }
  };

  // Idade a partir de nascimento (aceita 'DD/MM/YYYY' ou ISO)
  const calcIdade = (dateStr) => {
    if (!dateStr) return null;
    try {
      let d;
      const parts = String(dateStr).split('/');
      if (parts.length === 3) {
        d = new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]));
      } else {
        d = new Date(dateStr);
      }
      if (isNaN(d.getTime())) return null;
      const hoje = new Date();
      let idade = hoje.getFullYear() - d.getFullYear();
      const m = hoje.getMonth() - d.getMonth();
      if (m < 0 || (m === 0 && hoje.getDate() < d.getDate())) idade--;
      return idade >= 0 && idade < 130 ? idade : null;
    } catch {
      return null;
    }
  };

  const formatSexo = (s) => {
    if (!s) return null;
    const v = String(s).trim().toUpperCase();
    if (v === 'M' || v === 'MASCULINO') return 'Masculino';
    if (v === 'F' || v === 'FEMININO') return 'Feminino';
    return s;
  };

  const formatCPF = (cpf) => {
    if (!cpf) return '-';
    const cleaned = cpf.replace(/\D/g, '');
    if (cleaned.length === 11) {
      return `${cleaned.slice(0, 3)}.${cleaned.slice(3, 6)}.${cleaned.slice(6, 9)}-${cleaned.slice(9)}`;
    }
    return cpf;
  };

  // Endereço completo (Sit. 6 da AME): o atendente valida o cadastro na ligação,
  // e até aqui a ficha só mostrava cidade/UF.
  const formatCEP = (cep) => {
    const d = String(cep || '').replace(/\D/g, '').padStart(8, '0');
    return d.length === 8 && d !== '00000000' ? `${d.slice(0, 5)}-${d.slice(5)}` : null;
  };

  const formatEndereco = (e) => {
    if (!e) return null;
    const rua = [e.logradouro, e.numero].filter(Boolean).join(', ');
    const partes = [rua, e.complemento, e.bairro].filter(Boolean).join(' · ');
    const cidade = [e.cidade, e.uf].filter(Boolean).join('/');
    const cep = formatCEP(e.cep);
    const cauda = [cidade, cep].filter(Boolean).join(' · ');
    const texto = [partes, cauda].filter(Boolean).join(' · ');
    return texto || null;
  };

  const handleNovoAtendimento = () => {
    const benef = dados?.beneficiario;
    navigate('/kanban', {
      state: {
        abrirNovoAtendimento: true,
        beneficiario: benef ? {
          id: benef.id,
          nome_completo: benef.nome || benef.nome_completo,
          cpf: benef.cpf,
          cpf_mascarado: formatCPF(benef.cpf),
        } : null
      }
    });
  };

  const handleVerPessoa = (cpfPessoa) => {
    navigate(`/beneficiario-detalhe/${cpfPessoa}`);
  };

  const getStatusClass = (status) => {
    if (!status) return '';
    return status.toUpperCase() === 'ATIVO' ? 'status-ATIVO' : 'status-INATIVO';
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!dados || !dados.beneficiario) {
    return <div className="empty-state">Beneficiario nao encontrado</div>;
  }

  const { beneficiario, titular_vinculado, grupo_familiar, grupos_vinculados, atendimentos, total_atendimentos } = dados;

  // Helpers visuais reutilizáveis
  const S = {
    page: { padding: '24px', maxWidth: '1200px', margin: '0 auto' },
    card: { background: '#fff', borderRadius: '12px', border: '1px solid #e8e8e8', padding: '20px', marginBottom: '16px' },
    sectionTitle: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.72rem', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '14px' },
    badge: (bg, color) => ({ fontSize: '0.7rem', fontWeight: 700, padding: '2px 8px', borderRadius: '10px', background: bg, color }),
  };

  const todoContratos = [
    { contrato: beneficiario.contrato, status: beneficiario.status, planos: beneficiario.planos || [],
      responsavel_financeiro: beneficiario.responsavel_financeiro },
    ...(beneficiario.outros_contratos_ativos || []).map(c => ({ contrato: c.contrato, status: c.status, planos: c.planos || [], responsavel_financeiro: c.responsavel_financeiro })),
    ...(beneficiario.contratos_inativos || []).map(c => ({ contrato: c.contrato, status: c.status, planos: c.planos || [], responsavel_financeiro: c.responsavel_financeiro })),
  ];

  // Responsável financeiro por contrato (Sit. 7). Só repassamos informação
  // financeira ao RF, então o atendente precisa saber quem é — e conferir,
  // pelo CPF mascarado, se é com ele que está falando.
  const contratosComRF = todoContratos.filter(c => c.responsavel_financeiro?.nome);

  const carenciasAtivas = carencias.filter(c => !c.cumprida);
  const carenciasCumpridas = carencias.filter(c => c.cumprida);
  // Carência ativa que vence primeiro (pra prévia no resumo recolhido)
  const proximaCarencia = carenciasAtivas.length > 0
    ? carenciasAtivas.reduce((a, b) => (a.dias_restantes <= b.dias_restantes ? a : b))
    : null;

  const ehTitular = !titular_vinculado;

  const statusAtend = { ABERTO: '#3498db', EM_ANDAMENTO: '#f39c12', FINALIZADO: '#27ae60', TRANSFERIDO: '#9b59b6', AGUARDANDO_RETORNO: '#e74c3c' };
  const coresCanal = ['var(--brand, #20a490)', '#3498db', '#9b59b6', '#f39c12', '#e74c3c', '#1abc9c'];

  return (
    <div style={S.page}>

      {/* ── CABEÇALHO ─────────────────────────────────────── */}
      <div style={{ ...S.card, marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
          <button onClick={() => navigate('/busca')} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '6px 14px', cursor: 'pointer', fontSize: '0.85rem', color: '#555' }}>
            ← Voltar
          </button>
          <button onClick={handleNovoAtendimento} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'var(--brand, #20a490)', color: 'var(--brand-ink, #fff)', border: 'none', borderRadius: '8px', padding: '8px 18px', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem' }}>
            <Plus size={16} /> Novo Atendimento
          </button>
        </div>

        <div style={{ display: 'flex', gap: '18px', alignItems: 'flex-start' }}>
          {/* Avatar */}
          <div style={{ width: '60px', height: '60px', borderRadius: '50%', background: 'linear-gradient(135deg, var(--brand, #20a490), #1a8a78)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <User size={28} color="#fff" />
          </div>

          {/* Dados principais */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '0 0 8px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                <h1 style={{ margin: 0, fontSize: '1.3rem', color: '#1a1a2e', fontWeight: 700 }}>{beneficiario.nome}</h1>
                {beneficiario.nome_social && (
                  <span title="Nome Social (Lei 12.852/2013 · ANS RN 459/2021)" style={{ fontSize: '0.78rem', color: '#7c3aed', display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <span style={{ fontWeight: 600, opacity: 0.6, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Nome Social</span>
                    {beneficiario.nome_social}
                  </span>
                )}
              </div>
              <ScoreSaude
                maiorAtraso={inadimplencia.maior_atraso_dias}
                qtdCarenciasAtivas={carenciasAtivas.length}
                qtdDependentes={!titular_vinculado ? Math.max(0, (grupo_familiar?.length || 0) - 1) : 0}
                qtdContratosSubordinados={!titular_vinculado ? (grupos_vinculados?.filter(g => g.membros.some(m => m.status?.toUpperCase() === 'ATIVO')).length || 0) : 0}
                dividas={dividas}
                podeVerModulo={podeVerInadimplencia}
                onVerModulo={() => {
                  // Abre o modulo JA FILTRADO neste responsavel financeiro
                  // (a busca da tela aceita nome, CPF e matricula do RF).
                  const mat = beneficiario?.matricula_soul || beneficiario?.matricula || '';
                  navigate(`/dashboard/inadimplencia${mat ? `?busca=${encodeURIComponent(mat)}` : ''}`);
                }}
              />
            </div>

            {/* Contratos */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px', alignItems: 'center' }}>
              {todoContratos.map((c, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#f8f9fa', border: '1px solid #e8e8e8', borderRadius: '8px', padding: '4px 10px', fontSize: '0.8rem' }}>
                  <Building size={12} color="#888" />
                  <span style={{ color: '#555' }}>Contrato {c.contrato}</span>
                  {c.planos?.length > 0 && <span style={{ color: '#888' }}>· {c.planos.map(p => p.plano).filter(Boolean).join(' · ')}</span>}
                  {beneficiario.parentesco && i === 0 && <span style={{ color: '#aaa' }}>· {beneficiario.parentesco}</span>}
                  <span style={{ fontWeight: 700, color: c.status?.toUpperCase() === 'ATIVO' ? '#059669' : '#dc2626', fontSize: '0.72rem' }}>
                    {c.status?.toUpperCase() || 'INATIVO'}
                  </span>
                </div>
              ))}
            </div>

            {/* Responsável financeiro do contrato (Sit. 7) */}
            {contratosComRF.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
                {contratosComRF.map((c, i) => {
                  const rf = c.responsavel_financeiro;
                  const ehOProprio = rf.eh_o_titular
                    && (rf.nome || '').trim().toUpperCase() === (beneficiario.nome || '').trim().toUpperCase();
                  return (
                    <div key={i} title="Só o responsável financeiro pode receber informação financeira"
                      style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#fff8e7',
                        border: '1px solid #f0d9a8', borderRadius: '8px', padding: '4px 10px', fontSize: '0.8rem' }}>
                      <Wallet size={12} color="#b8860b" />
                      <span style={{ color: '#8a6d1f', fontWeight: 600 }}>Resp. financeiro</span>
                      {todoContratos.length > 1 && <span style={{ color: '#aaa' }}>· contrato {c.contrato}</span>}
                      <span style={{ color: '#555' }}>
                        {ehOProprio ? 'o próprio beneficiário' : rf.nome}
                      </span>
                      {!ehOProprio && rf.cpf_mascarado && (
                        <span style={{ color: '#888' }}>· {rf.cpf_mascarado}</span>
                      )}
                      {rf.dia_vencimento && (
                        <span style={{ color: '#aaa' }}>· vence dia {rf.dia_vencimento}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Contatos */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', fontSize: '0.82rem', color: '#555' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><CreditCard size={13} color="#aaa" /> {formatCPF(beneficiario.cpf)}</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><Calendar size={13} color="#aaa" /> {formatDate(beneficiario.nascimento)}{calcIdade(beneficiario.nascimento) != null && <span style={{ color: '#888' }}> · {calcIdade(beneficiario.nascimento)} anos</span>}</span>
              {formatSexo(beneficiario.sexo) && <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><User size={13} color="#aaa" /> {formatSexo(beneficiario.sexo)}</span>}
              {beneficiario.adesao && <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><Calendar size={13} color="#aaa" /> Adesão {formatDate(beneficiario.adesao)}</span>}
              {beneficiario.celular && <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><Phone size={13} color="#aaa" /> {beneficiario.celular}</span>}
              {beneficiario.telefone_fixo && <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><Phone size={13} color="#aaa" /> {beneficiario.telefone_fixo} <span style={{ fontSize: '0.68rem', color: '#bbb' }}>fixo</span></span>}
              {beneficiario.email && <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><Mail size={13} color="#aaa" /> {beneficiario.email}</span>}
              {beneficiario.email_2 && <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><Mail size={13} color="#aaa" /> {beneficiario.email_2}</span>}
              <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><CreditCard size={13} color="#aaa" /> Mat. {beneficiario.matricula || '-'}</span>
            </div>

            {/* Endereço completo (Sit. 6) — para validar o cadastro na ligação */}
            {(formatEndereco(beneficiario.endereco) || beneficiario.cidade) && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '5px', marginTop: '8px',
                fontSize: '0.82rem', color: '#555' }}>
                <MapPin size={13} color="#aaa" style={{ marginTop: '2px', flexShrink: 0 }} />
                <span>{formatEndereco(beneficiario.endereco)
                  || `${beneficiario.cidade}/${beneficiario.uf}`}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Idade do dado: só aparece se alguma cópia local estiver defasada */}
      <AvisoDefasagem />

      {/* ── ALERTAS DO ERP (Sit. 13) — antes de qualquer outra informação ── */}
      <AlertasBeneficiario cpf={beneficiario.cpf} />

      {/* ── STAT CARDS ────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '16px' }}>
        {[
          { icon: <FileText size={20} color="var(--brand, #20a490)" />, valor: dashboard?.total_atendimentos ?? total_atendimentos, label: 'Atendimentos', cor: 'var(--brand, #20a490)' },
          { icon: <Clock size={20} color="#3498db" />, valor: dashboard?.tempo_medio_horas ? formatHoras(dashboard.tempo_medio_horas) : '-', label: 'Tempo Médio', cor: '#3498db' },
          { icon: <Star size={20} color={dashboard?.nps_medio ? getNPSColor(dashboard.nps_medio) : '#aaa'} />, valor: dashboard?.nps_medio != null ? dashboard.nps_medio.toFixed(1) : '-', label: 'NPS Médio', cor: dashboard?.nps_medio ? getNPSColor(dashboard.nps_medio) : '#aaa' },
          { icon: <Users size={20} color="#9b59b6" />, valor: grupo_familiar?.length || 0, label: 'No Grupo', cor: '#9b59b6' },
        ].map((s, i) => (
          <div key={i} style={{ ...S.card, marginBottom: 0, display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{ width: '44px', height: '44px', borderRadius: '10px', background: `${s.cor}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              {s.icon}
            </div>
            <div>
              <div style={{ fontSize: '1.4rem', fontWeight: 700, color: s.cor, lineHeight: 1 }}>{s.valor}</div>
              <div style={{ fontSize: '0.75rem', color: '#999', marginTop: '2px' }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── GRID PRINCIPAL ────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '380px 1fr', gap: '16px', alignItems: 'start' }}>

        {/* COLUNA ESQUERDA */}
        <div>

          {/* Cuidados e IDSS — só para quem tem Indicadores ANS (informação de saúde) */}
          {podeModulo('indicadores_ans') && <CuidadosIdss cpf={cpf} cardStyle={S.card} tituloStyle={S.sectionTitle} />}

          {/* Carências - estado de falha (antes ficava silencioso) */}
          {carenciasErro && (
            <div style={S.card}>
              <div style={S.sectionTitle}>
                <ShieldAlert size={14} />
                Carências
              </div>
              <div style={{ background: '#fff8f8', border: '1px solid #ffcdd2', borderRadius: '10px', padding: '12px', fontSize: '0.8rem', color: '#b71c1c' }}>
                Carências indisponíveis no momento.
                <div style={{ fontSize: '0.7rem', color: '#c62828', marginTop: '4px' }}>{carenciasErro}</div>
              </div>
            </div>
          )}

          {/* Carências - consulta ok e nada registrado (antes o quadro sumia e
              não dava para distinguir "sem carência" de "não carregou") */}
          {carenciasCarregadas && !carenciasErro && carencias.length === 0 && (
            <div style={S.card}>
              <div style={S.sectionTitle}>
                <ShieldAlert size={14} />
                Carências
              </div>
              <div style={{ fontSize: '0.8rem', color: '#7f8c8d' }}>
                Nenhuma carência registrada no sistema da operadora.
              </div>
            </div>
          )}

          {/* Carências */}
          {!carenciasErro && carencias.length > 0 && (
            <div style={S.card}>
              <div style={S.sectionTitle}>
                <ShieldAlert size={14} />
                Carências
              </div>

              {carenciasAtivas.length > 0 && (
                <details style={{ marginBottom: carenciasCumpridas.length > 0 ? '6px' : 0 }}>
                  <summary style={{ fontSize: '0.82rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', userSelect: 'none', listStyle: 'none' }}>
                    <ShieldAlert size={13} color="#c62828" />
                    <span style={{ fontWeight: 700, color: '#b71c1c' }}>{carenciasAtivas.length} em vigor</span>
                    {proximaCarencia && (
                      <span style={{ fontWeight: 400, color: '#aaa' }}>· próxima vence {proximaCarencia.dt_vencimento}</span>
                    )}
                    <ChevronDown size={14} color="#c62828" style={{ marginLeft: 'auto' }} />
                  </summary>
                  <div style={{ marginTop: '8px' }}>
                    {carenciasAtivas.map((c, i) => {
                      const dtI = c.dt_inicio ? c.dt_inicio.split('/').reverse().join('-') : null;
                      const dtV = c.dt_vencimento ? c.dt_vencimento.split('/').reverse().join('-') : null;
                      const total = dtI && dtV ? Math.max(1, Math.round((new Date(dtV) - new Date(dtI)) / 86400000)) : 1;
                      const pct = Math.min(100, Math.round(((total - c.dias_restantes) / total) * 100));
                      return (
                        <div key={i} style={{ background: '#fff8f8', border: '1px solid #ffcdd2', borderRadius: '10px', padding: '12px', marginBottom: '8px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                            <span style={{ fontWeight: 600, fontSize: '0.82rem', color: '#b71c1c' }}>{c.descricao}</span>
                            <span style={S.badge('#c62828', '#fff')}>{c.dias_restantes}d restantes</span>
                          </div>
                          <div style={{ fontSize: '0.74rem', color: '#aaa', marginBottom: '7px' }}>{c.dt_inicio} → {c.dt_vencimento}</div>
                          <div style={{ background: '#ffcdd2', borderRadius: '4px', height: '5px' }}>
                            <div style={{ width: `${pct}%`, height: '100%', background: '#e53935', borderRadius: '4px' }} />
                          </div>
                          <div style={{ fontSize: '0.68rem', color: '#bbb', textAlign: 'right', marginTop: '2px' }}>{pct}% cumprida</div>
                        </div>
                      );
                    })}
                  </div>
                </details>
              )}

              {carenciasCumpridas.length > 0 && (
                <details style={{ marginTop: '4px' }}>
                  <summary style={{ fontSize: '0.78rem', color: '#888', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', userSelect: 'none', listStyle: 'none' }}>
                    <ShieldCheck size={13} color="#2e7d32" /> {carenciasCumpridas.length} já cumprida{carenciasCumpridas.length > 1 ? 's' : ''}
                  </summary>
                  <div style={{ marginTop: '6px', display: 'grid', gap: '5px' }}>
                    {carenciasCumpridas.map((c, i) => (
                      <div key={i} style={{ background: '#f1f8e9', border: '1px solid #c5e1a5', borderRadius: '8px', padding: '7px 12px', display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: '0.78rem', color: '#33691e' }}><ShieldCheck size={12} style={{ verticalAlign: 'text-bottom', marginRight: '4px' }} />{c.descricao}</span>
                        <span style={{ fontSize: '0.72rem', color: '#558b2f' }}>Venceu {c.dt_vencimento}</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}

          {/* Titular Vinculado */}
          {titular_vinculado && (
            <div style={S.card}>
              <div style={S.sectionTitle}><LinkIcon size={14} /> Vinculado ao Titular</div>
              <div onClick={() => handleVerPessoa(titular_vinculado.cpf)} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px', background: '#f8f9fa', borderRadius: '8px', cursor: 'pointer', border: '1px solid #e8e8e8' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#e3f2fd', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><User size={18} color="#1565c0" /></div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#1a1a2e' }}>{titular_vinculado.nome}</div>
                  <div style={{ fontSize: '0.75rem', color: '#888' }}>{titular_vinculado.parentesco} · Contrato {titular_vinculado.contrato}</div>
                </div>
                <ChevronRight size={16} color="#ccc" />
              </div>
            </div>
          )}

          {/* Grupo Familiar */}
          <div style={S.card}>
            <div style={S.sectionTitle}>
              <Users size={14} /> Grupo Familiar
              {beneficiario.contrato && <span style={{ fontWeight: 400, color: '#aaa', textTransform: 'none', letterSpacing: 0 }}>Contrato {beneficiario.contrato}</span>}
            </div>
            {grupo_familiar && grupo_familiar.length > 0 ? (() => {
              // Agrupar por CPF — cada pessoa aparece uma vez com todos os seus planos
              const membrosMap = {};
              for (const p of grupo_familiar) {
                const key = p.cpf || p.matricula_soul;
                if (!membrosMap[key]) {
                  membrosMap[key] = { ...p, planos: [] };
                }
                if (p.plano && !membrosMap[key].planos.includes(p.plano)) {
                  membrosMap[key].planos.push(p.plano);
                }
              }
              const membrosUnicos = Object.values(membrosMap).sort((a, b) => {
                const rank = (m) => {
                  const isSelf = m.cpf === beneficiario.cpf;
                  const isTitular = m.parentesco?.toUpperCase() === 'TITULAR';
                  const isInativo = m.status?.toUpperCase() !== 'ATIVO';
                  if (isInativo) return 3;
                  if (isSelf || isTitular) return 0;
                  return 2;
                };
                return rank(a) - rank(b);
              });
              return (
                <div style={{ display: 'grid', gap: '6px' }}>
                  {membrosUnicos.map((p, i) => {
                    const isSelf = p.cpf === beneficiario.cpf;
                    const isInativo = p.status?.toUpperCase() !== 'ATIVO';
                    return (
                      <div key={i} onClick={() => !isSelf && p.cpf && handleVerPessoa(p.cpf)}
                        style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 10px', borderRadius: '8px', border: `1px solid ${isSelf ? 'var(--brand, #20a490)30' : isInativo ? '#ffcdd2' : '#e8e8e8'}`, background: isSelf ? '#f0faf8' : isInativo ? '#fff8f8' : '#fafafa', cursor: !isSelf && p.cpf ? 'pointer' : 'default', opacity: isInativo ? 0.7 : 1 }}>
                        <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: isSelf ? 'var(--brand, #20a490)' : isInativo ? '#ffcdd2' : '#e8e8e8', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <User size={16} color={isSelf ? '#fff' : isInativo ? '#c62828' : '#888'} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: '0.82rem', color: isInativo ? '#999' : '#1a1a2e' }}>
                            <span style={{ textDecoration: isInativo ? 'line-through' : 'none' }}>{p.nome}</span>
                          </div>
                          <div style={{ fontSize: '0.73rem', color: '#aaa' }}>
                            {p.parentesco}{p.planos.length > 0 ? ' · ' + p.planos.join(' · ') : ''}
                          </div>
                        </div>
                        {isInativo && <span style={S.badge('#fce4ec', '#c62828')}>INATIVO</span>}
                        {!isSelf && p.cpf && <ChevronRight size={14} color="#ccc" />}
                      </div>
                    );
                  })}
                </div>
              );
            })() : (
              <div style={{ color: '#aaa', fontSize: '0.85rem', textAlign: 'center', padding: '20px 0' }}>Nenhum membro no grupo familiar</div>
            )}
          </div>

          {/* Grupos Vinculados (RF) */}
          {grupos_vinculados && grupos_vinculados.length > 0 && (
            <div style={S.card}>
              <div style={S.sectionTitle}><LinkIcon size={14} /> Grupos Vinculados (RF)</div>
              {grupos_vinculados.map((grupo, i) => {
                const todoInativos = grupo.membros.length > 0 && grupo.membros.every(m => m.status?.toUpperCase() !== 'ATIVO');
                return (
                <div key={i} style={{ marginBottom: i < grupos_vinculados.length - 1 ? '12px' : 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#888', marginBottom: '6px', padding: '0 2px' }}>
                    <span style={{ fontWeight: 600, color: todoInativos ? '#e74c3c' : '#555' }}>Contrato {grupo.contrato}</span>
                    <span>{grupo.plano}</span>
                  </div>
                  {grupo.responsavel_financeiro && (
                    <div onClick={() => handleVerPessoa(grupo.responsavel_financeiro.cpf)} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', background: '#fff3e0', border: '1px solid #ffe0b2', borderRadius: '8px', cursor: 'pointer', marginBottom: '5px' }}>
                      <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: '#ff9800', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><User size={15} color="#fff" /></div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.8rem', color: '#1a1a2e' }}>{grupo.responsavel_financeiro.nome}</div>
                        <div style={{ fontSize: '0.7rem', color: '#e65100' }}>Resp. Financeiro</div>
                      </div>
                      <ChevronRight size={14} color="#ccc" />
                    </div>
                  )}
                  {grupo.membros.filter(m => !grupo.responsavel_financeiro || m.cpf !== grupo.responsavel_financeiro.cpf).map((dep, j) => (
                    <div key={j} onClick={() => handleVerPessoa(dep.cpf)} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '7px 10px', background: '#fafafa', border: '1px solid #e8e8e8', borderRadius: '8px', cursor: 'pointer', marginBottom: '4px' }}>
                      <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#e8e8e8', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><User size={14} color="#888" /></div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 500, fontSize: '0.8rem', color: '#333' }}>{dep.nome}</div>
                        <div style={{ fontSize: '0.7rem', color: '#aaa' }}>{dep.parentesco}</div>
                      </div>
                      <ChevronRight size={14} color="#ccc" />
                    </div>
                  ))}
                </div>
                );
              })}
            </div>
          )}
        </div>

        {/* COLUNA DIREITA */}
        <div>

          {/* Panorama */}
          {dashboard && dashboard.total_atendimentos > 0 && (
            <div style={S.card}>
              <button onClick={() => setShowDashboard(!showDashboard)} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', cursor: 'pointer', padding: 0, width: '100%' }}>
                <div style={{ ...S.sectionTitle, marginBottom: 0, flex: 1 }}><BarChart3 size={14} /> Panorama</div>
                {showDashboard ? <ChevronUp size={15} color="#aaa" /> : <ChevronDown size={15} color="#aaa" />}
              </button>

              {showDashboard && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '14px' }}>
                  {/* Por Canal */}
                  {dashboard.por_canal.length > 0 && (
                    <div style={{ background: '#fafafa', borderRadius: '10px', padding: '14px', border: '1px solid #f0f0f0' }}>
                      <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>Canais</div>
                      {dashboard.por_canal.map((c, i) => {
                        const pct = Math.round((c.total / dashboard.total_atendimentos) * 100);
                        return (
                          <div key={i} style={{ marginBottom: '8px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', marginBottom: '3px' }}>
                              <span style={{ color: '#555' }}>{canalLabels[c.canal] || c.canal}</span>
                              <span style={{ fontWeight: 600, color: '#333' }}>{c.total}</span>
                            </div>
                            <div style={{ height: '5px', borderRadius: '3px', background: '#e8e8e8' }}>
                              <div style={{ height: '100%', borderRadius: '3px', background: coresCanal[i % coresCanal.length], width: `${pct}%` }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Por Categoria */}
                  {dashboard.por_categoria.length > 0 && (
                    <div style={{ background: '#fafafa', borderRadius: '10px', padding: '14px', border: '1px solid #f0f0f0' }}>
                      <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>Categorias</div>
                      {dashboard.por_categoria.map((c, i) => {
                        const pct = Math.round((c.total / dashboard.total_atendimentos) * 100);
                        return (
                          <div key={i} style={{ marginBottom: '8px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', marginBottom: '3px' }}>
                              <span style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#555' }}>
                                <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: c.cor, flexShrink: 0 }} />
                                {c.categoria}
                              </span>
                              <span style={{ fontWeight: 600, color: '#333' }}>{c.total}</span>
                            </div>
                            <div style={{ height: '5px', borderRadius: '3px', background: '#e8e8e8' }}>
                              <div style={{ height: '100%', borderRadius: '3px', background: c.cor, width: `${pct}%` }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Por Status */}
                  {dashboard.por_status.length > 0 && (
                    <div style={{ background: '#fafafa', borderRadius: '10px', padding: '14px', border: '1px solid #f0f0f0' }}>
                      <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>Status</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {dashboard.por_status.map((s, i) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#fff', border: '1px solid #e8e8e8', borderRadius: '8px', padding: '5px 10px', flex: '1', minWidth: '100px' }}>
                            <span style={{ fontWeight: 700, fontSize: '1rem', color: statusAtend[s.status] || '#999' }}>{s.total}</span>
                            <span style={{ fontSize: '0.73rem', color: '#777' }}>{statusLabels[s.status] || s.status}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* NPS Histórico */}
                  {dashboard.nps_historico.length > 0 && (
                    <div style={{ background: '#fafafa', borderRadius: '10px', padding: '14px', border: '1px solid #f0f0f0' }}>
                      <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>Histórico NPS</div>
                      {dashboard.nps_historico.map((n, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 0', borderBottom: i < dashboard.nps_historico.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                          <span style={{ width: '32px', height: '32px', borderRadius: '50%', background: getNPSColor(n.nota), color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.85rem', flexShrink: 0 }}>{n.nota}</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '0.78rem', fontWeight: 600, color: getNPSColor(n.nota) }}>{getNPSLabel(n.nota)}</div>
                            {n.feedback && <div style={{ fontSize: '0.73rem', color: '#aaa', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>"{n.feedback}"</div>}
                          </div>
                          {n.data && <span style={{ fontSize: '0.72rem', color: '#bbb', flexShrink: 0 }}>{new Date(n.data).toLocaleDateString('pt-BR')}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Cartoes de Saude (nasce colapsado, fetch so na 1a expansao) */}
          <CartoesSaude cpf={beneficiario.cpf} />

          {/* Atendimentos */}
          <div style={S.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
              <div style={S.sectionTitle}><FileText size={14} /> Atendimentos</div>
              {total_atendimentos > 0 && <span style={S.badge('#f0f0f0', '#666')}>{total_atendimentos} total</span>}
            </div>

            {atendimentos && atendimentos.length > 0 ? (
              <>
                <div style={{ display: 'grid', gap: '8px' }}>
                  {(showAllAtendimentos ? atendimentos : atendimentos.slice(0, 5)).map((atend, i) => (
                    <div key={i} onClick={() => navigate(`/atendimento/${atend.id}`)} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', background: '#fafafa', border: '1px solid #e8e8e8', borderRadius: '9px', cursor: 'pointer', transition: 'border-color 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--brand, #20a490)'}
                      onMouseLeave={e => e.currentTarget.style.borderColor = '#e8e8e8'}>
                      <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: statusAtend[atend.status] || '#aaa', flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                          <span style={{ fontWeight: 600, fontSize: '0.8rem', color: '#333' }}>{atend.protocolo}</span>
                          <span style={{ fontSize: '0.72rem', color: '#aaa' }}>{formatDate(atend.data_abertura)}</span>
                        </div>
                        <div style={{ fontSize: '0.78rem', color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{atend.motivo}</div>
                      </div>
                      <span style={S.badge(`${statusAtend[atend.status] || '#aaa'}18`, statusAtend[atend.status] || '#666')}>{(atend.status || '').replace('_', ' ')}</span>
                      <ChevronRight size={14} color="#ccc" />
                    </div>
                  ))}
                </div>
                {atendimentos.length > 5 && (
                  <button onClick={() => setShowAllAtendimentos(!showAllAtendimentos)} style={{ width: '100%', marginTop: '10px', padding: '8px', background: 'none', border: '1px solid #e8e8e8', borderRadius: '8px', cursor: 'pointer', fontSize: '0.8rem', color: '#666', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                    {showAllAtendimentos ? <><ChevronUp size={14} /> Ver menos</> : <><ChevronDown size={14} /> Ver mais ({atendimentos.length - 5} restantes)</>}
                  </button>
                )}
              </>
            ) : (
              <div style={{ color: '#aaa', fontSize: '0.85rem', textAlign: 'center', padding: '30px 0' }}>Nenhum atendimento registrado</div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default BeneficiarioDetalhe;
